/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { connect } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import Auth from '@aws-amplify/auth';
import { FiMoreVertical, FiX } from 'react-icons/fi';

import { authStatuses, assetsUrl, stripeEnv } from '../util/constants';
import { AuthConditionalRender } from '../auth/AuthConditionalRender';
import { getAllExerciseProgressSummary } from '../exercises/exerciseData';
import { getSubscriptionsFromState } from '../util/getSubscriptionsFromState';
import { Button } from '../components/Button';
import { ModulesList } from './ModulesList';
import { ExerciseProgress } from './ExerciseProgress';
import { ExerciseIcon } from '../branding/ExerciseIcon';
import { Title } from '../components/Title';
import { RoundLogo } from '../branding/RoundLogo';
import { Navigation } from '../components/Navigation';
import { ContentScreen } from '../styles/ContentScreen';
import { useMemo } from 'react';
import { Onboarding } from './Onboarding';
import { initialExerciseProgress } from '../exercises/initialExerciseProgress';
import { apiRequest } from '../util/apiRequest';
import { ShowSiteLoading } from '../SiteLoadingIndicator';
import { useQueries } from '@tanstack/react-query';
import {
   courseDocumentQueryOptions,
   courseProgressQueryOptions,
} from '../courses/queryGroups';
import { useProcessedLessonProgress } from '../courses/useProcessedLessonProgress';

const DashboardStyles = styled(ContentScreen)`
   .review-items {
      display: grid;
      grid-template-columns: 1fr;
      gap: 3em;
      margin-bottom: 3em;
      margin-top: 1em;

      article {
         max-width: 600px;
         overflow: auto;

         p {
            text-wrap: pretty;
         }
      }
   }

   @media (min-width: 1000px) {
      .review-items {
         grid-template-columns: 1fr 1fr;
      }
   }

   @media (min-width: 800px) {
      .exercise-items {
         display: grid;
         grid-template-columns: 1fr 1fr;
         grid-gap: 3em;
      }
   }

   .exercise-item-display {
      margin-bottom: 3em;
      overflow: auto;

      @media (min-width: 800px) {
         margin-bottom: 1em;
      }

      h2 {
         font-size: 1.75em;

         margin-bottom: 1rem;
         white-space: nowrap;
         text-overflow: ellipsis;
         overflow: hidden;
      }

      .progress-wrapper {
         width: 200px;
         margin: 0 auto;
      }

      .description-wrapper {
         max-width: 400px;
         margin: auto;
      }

      .description-wrapper,
      .description-wrapper .description-text {
         display: flex;
         align-items: center;
      }

      .description-and-icon {
         display: grid;
         grid-template-columns: 75px 1fr;
         grid-gap: 1.5em;
         margin-bottom: 2em;

         margin-bottom: 1.5em;
      }

      @media (min-width: 650px) {
         .progress-item-data {
            display: grid;
            grid-template-columns: 200px 1fr;
            grid-gap: 1em 2em;
         }

         .description-wrapper {
            margin: 0;
         }
      }

      @media (min-width: 800px) {
         .progress-item-data {
            display: block;
         }

         .description-wrapper {
            margin: auto;
         }
      }

      @media (min-width: 1200px) {
         .progress-item-data {
            display: grid;
            grid-template-columns: 200px 1fr;
            grid-gap: 1em 2em;
         }

         .description-wrapper {
            margin: 0;
         }
      }

      .icon-wrapper {
         display: flex;
         align-items: center;

         .svg-icon {
            width: 75px;
         }
      }

      .description-text {
         line-height: 1.4;
      }

      .exercise-item-buttons {
         display: grid;
         grid-template-columns: 1fr 1fr;
         grid-gap: 1em;
      }

      .modules-wrapper {
         grid-column-end: span 2;

         h3 {
            font-size: 1.25em;
            margin-bottom: 0.5em;
            color: var(--brand-light-green);

            margin-top: 1.5em;
         }

         @media (prefers-color-scheme: dark) {
            h3 {
               color: var(--brand-green);
            }
         }

         @media (min-width: 650px) {
            h3 {
               margin-top: 0;
            }
         }

         @media (min-width: 800px) {
            h3 {
               margin-top: 1.5em;
            }
         }

         @media (min-width: 1200px) {
            h3 {
               margin-top: 0;
            }
         }

         .exercise-accuracy-cell,
         .exercise-accuracy-head,
         .exercise-progress-cell,
         .exercise-progress-head {
            display: none;
         }

         @media (min-width: 450px) {
            .exercise-progress-cell,
            .exercise-progress-head {
               display: table-cell;
            }
         }

         @media (min-width: 650px) {
            .exercise-accuracy-cell,
            .exercise-accuracy-head {
               display: table-cell;
            }
         }

         @media (min-width: 800px) {
            .exercise-accuracy-cell,
            .exercise-accuracy-head,
            .exercise-progress-cell,
            .exercise-progress-head {
               display: none;
            }
         }

         @media (min-width: 950px) {
            .exercise-progress-cell,
            .exercise-progress-head {
               display: table-cell;
            }
         }

         @media (min-width: 1250px) {
            .exercise-accuracy-cell,
            .exercise-accuracy-head {
               display: table-cell;
            }
         }
      }
   }

   @media (min-width: 800px) {
      .exercise-item-display:nth-child(2n) {
         .module-list {
            border-left: 2px solid var(--brand-light-green);
         }
      }
   }
`;

const mapStateToExerciseDashboardItemProps = (state) => ({
   activeSubscriptions: getSubscriptionsFromState(state),
});

const ExerciseDashboardItem = connect(mapStateToExerciseDashboardItemProps)(
   ({ activeSubscriptions, exerciseInfo, exerciseSummary }) => {
      const [exercise] = useState(exerciseSummary);
      const [modules, setModules] = useState(null);

      const calculateFromAux =
         exerciseSummary &&
         exerciseSummary.currentLevel === exerciseInfo.levelCount &&
         exerciseInfo.hasModules;
      const hasProgress = exercise && exercise.accuracy != null;

      if (exerciseInfo == null || exerciseSummary == null) {
         return <p>Loading</p>;
      }

      return (
         <div className="exercise-item-display">
            <h2>{exerciseInfo.title}</h2>
            <div className="progress-item-data">
               <div>
                  <ExerciseProgress
                     hasProgress={hasProgress}
                     exerciseInfo={exerciseInfo}
                     exerciseProgress={exerciseSummary}
                     calculateFromAux={calculateFromAux}
                  />
               </div>
               <div className="description-wrapper">
                  <div>
                     <div className="description-and-icon">
                        <div className="icon-wrapper">
                           <ExerciseIcon iconId={exerciseInfo.iconId} />
                        </div>
                        <p className="description-text">
                           {exerciseInfo.description}
                        </p>
                     </div>
                     <div className="exercise-item-buttons">
                        <Button
                           as={Link}
                           className="button-cta"
                           to={`/app/exercise/${exerciseInfo.id}`}
                        >
                           Start Training
                        </Button>
                        <Button
                           as={Link}
                           to={`/app/catalog/exercise/${exerciseInfo.id}`}
                        >
                           View Details
                        </Button>
                     </div>
                  </div>
               </div>
               {modules && modules.length > 0 && (
                  <div className="modules-wrapper">
                     <h3>Modules</h3>
                     <ModulesList
                        exerciseId={exerciseInfo.id}
                        modules={modules}
                     />
                  </div>
               )}
            </div>
         </div>
      );
   }
);

const mapStateToProps = ({
   appData: {
      userData: { status },
   },
}) => ({
   loggedIn: status === authStatuses.authenticated,
});

const Header = styled.header`
   position: sticky;
   top: var(--safe-area-inset-top);
   width: var(--safe-width);
   z-index: 999;
   pointer-events: none;
   display: flex;
   justify-content: space-between;

   svg {
      height: var(--header-inner-height);
      overflow: visible;
   }

   .svg-color-background {
      transition: fill 0.25s ease;
   }

   a,
   button {
      pointer-events: auto;
   }

   @media (prefers-color-scheme: dark) {
      .svg-color-2 {
         fill: #001818;
      }
   }

   .logo-link {
      height: var(--header-inner-height);
      margin: var(--header-margin);
   }

   .logo-link:hover .svg-color-background,
   .logo-link:focus-visible .svg-color-background {
      fill: var(--brand-light-orange);
   }

   .header-right-content,
   .header-left-content {
      display: flex;
   }

   .login-link-container {
      display: flex;
      align-items: center;
   }

   .login-link {
      padding: 0.5em 1.5em;
      border-radius: 1.12em;
      background: var(--background-color);
      box-shadow: 0 0 0 9px var(--background-color);
   }

   .open-menu-button {
      color: var(--text-color);
      background-color: var(--background-color);
      border: none;
      cursor: pointer;
      padding: 0;
      width: var(--header-inner-height);
      height: var(--header-inner-height);
      margin: var(--header-margin);
      border-radius: 10px;
      transition: color 0.25s ease;
   }

   .open-menu-button:hover,
   .open-menu-button:focus-visible {
      color: var(--brand-orange);
   }
`;

const NavStyles = styled.header`
   ul {
      max-height: calc(var(--visible-and-safe-height) - 20px - 2em);
      padding: 30px 0;
      overflow: auto;
      margin: 1em;
      overscroll-behavior: contain;
   }
   a {
      font-family: var(--heading-font);
      display: inline-block;
      padding: 0.5em;
      text-decoration: none;
      width: 100%;
      color: var(--text-color);
   }
   a:hover,
   a:focus-visible {
      color: var(--brand-orange);
   }
   .login-link {
      font-family: var(--content-font);
   }

   .close-menu-button {
      color: var(--text-color);
      background-color: var(--background-color);
      border: none;
      cursor: pointer;
      padding: 0;
      width: 50px;
      height: 50px;
      margin: 10px;
      border-radius: 10px;
      transition: color 0.25s ease;
      position: absolute;
      top: 0px;
      right: 0px;
   }

   .close-menu-button:hover,
   .close-menu-button:focus-visible {
      color: var(--brand-orange);
   }

   .logout-button {
      width: 100%;
      padding: 0.5em;
   }
   .logout-button-li {
      padding: 0.5em;
   }
`;

export const HideHeader = createGlobalStyle`
   ${Header} {
      display: none;
   }
`;

export const MinimalHeader = createGlobalStyle`
   ${Header} {

      @media  (max-height: 550px) {

         position: fixed;
         
         .logo-link, .login-link-container {
            display: none;
         }

      }
   }
`;

export const SiteHeader = connect(mapStateToProps)(({ loggedIn }) => {
   const [showNavigation, setShowNavigation] = useState(false);

   return (
      <>
         <Header>
            {/* <HorizontalLogo /> */}
            <div className="header-left-content">
               <Link
                  to={loggedIn ? '/app/dashboard' : '/'}
                  className="logo-link"
               >
                  <RoundLogo background />
               </Link>
            </div>
            <div className="header-right-content">
               {/* {!loggedIn && (
                  <div className="login-link-container">
                     <Button as={Link} to="/app/login" className="login-link">
                        Log In
                     </Button>
                  </div>
               )} */}
               {loggedIn && (
                  <button
                     type="button"
                     className="open-menu-button"
                     onClick={() => setShowNavigation((shown) => !shown)}
                  >
                     <FiMoreVertical size="30px" />
                  </button>
               )}
            </div>
         </Header>
         <Navigation
            display={showNavigation}
            onOverlayClick={() => setShowNavigation(false)}
         >
            <NavStyles
               onClick={(e) => {
                  if (['a'].includes(e.target.tagName.toLowerCase())) {
                     setShowNavigation(false);
                  }
               }}
            >
               <button
                  type="button"
                  className="close-menu-button"
                  onClick={() => {
                     setShowNavigation(false);
                  }}
               >
                  <FiX size="30px" />
               </button>
               <ul>
                  {loggedIn && (
                     <li>
                        <Link to="/app/dashboard">Dashboard</Link>
                     </li>
                  )}
                  <li>
                     <Link to="/app/catalog/exercise">Exercises</Link>
                  </li>
                  {/* <li>
                     <Link to="/app/weekly-challenge">Weekly Challenge</Link>
                  </li> */}
                  <li>
                     <Link to="/app/resources/chord-progression-generator">
                        Chord Generator
                     </Link>
                  </li>
                  {/* <li>
                     <Link to="/app/downloads">Downloads</Link>
                  </li> */}

                  {/* {loggedIn && (
                     <li>
                        <Link to="/app/account">Account</Link>
                     </li>
                  )} */}
                  {loggedIn && (
                     <li>
                        <a href={stripeEnv.billingConsoleLink} target="_blank">
                           Account
                        </a>
                     </li>
                  )}
                  {loggedIn ? (
                     <li className="logout-button-li">
                        <Button
                           onClick={() => {
                              setShowNavigation(false);
                              Auth.signOut();
                           }}
                           className="logout-button"
                        >
                           Log Out
                        </Button>
                     </li>
                  ) : (
                     <li className="logout-button-li">
                        <Button
                           as={Link}
                           to="/app/login"
                           className="login-link"
                        >
                           Log In
                        </Button>
                     </li>
                  )}
               </ul>
            </NavStyles>
         </Navigation>
      </>
   );
});

const Dashboard = () => {
   const [exercises, setExercises] = useState(null);
   const [exerciseSummaries, setExerciseSummaries] = useState(null);

   const [dashboardData, setDashboardData] = useState(null);
   const [dashboardRelatedData, setDashboardRelatedData] = useState(false);

   const courseId = 'mixable';

   const [{ data: courseDocument }, { data: courseProgress }] = useQueries({
      queries: [
         courseDocumentQueryOptions(courseId),
         courseProgressQueryOptions(courseId),
      ],
   });

   useEffect(() => {
      apiRequest({ path: '/dashboard' }).then(({ links, ...dashboard }) => {
         setDashboardData(dashboard);
         setDashboardRelatedData({
            onboardingIncomplete:
               Object.values(dashboard.onboarding).findIndex(
                  (status) => status !== 'complete'
               ) >= 0,
            links,
         });
      });
   }, []);

   const handleOnboardingUpdate = useCallback(
      (item, newStatus) => {
         const newDashboardData = {
            ...dashboardData,
            onboarding: {
               ...dashboardData.onboarding,
               [item]: newStatus,
            },
         };
         setDashboardData(newDashboardData);
         apiRequest({
            path: '/dashboard',
            method: 'PUT',
            body: newDashboardData,
         });
      },
      [dashboardData, setDashboardData]
   );

   useEffect(() => {
      getAllExerciseProgressSummary().then(setExerciseSummaries);
      fetch(`${assetsUrl}/public/exercise/exerciseList.json`)
         .then((res) => res.json())
         .then((ex) =>
            setExercises(
               ex.sort(({ hasModules: a }, { hasModules: b }) => {
                  if (a && !b) {
                     return 1;
                  }
                  if (b && !a) {
                     return -1;
                  }
                  return 0;
               })
            )
         );
   }, []);

   if (
      dashboardData == null ||
      courseDocument == null ||
      courseProgress == null ||
      exerciseSummaries == null
   ) {
      return <ShowSiteLoading />;
   }

   return (
      <>
         <Title title="Dashboard" />
         <DashboardStyles className="wide">
            {/* <h1>Dashboard</h1> */}
            <div className="review-items">
               {/* {dashboardRelatedData.onboardingIncomplete && (
                  <Onboarding
                     progress={dashboardData.onboarding}
                     onUpdateItem={handleOnboardingUpdate}
                  />
               )} */}
               <CoursePrompt
                  courseDocument={courseDocument}
                  courseProgress={courseProgress}
               />
               {(courseProgress.length > 0 ||
                  Object.keys(exerciseSummaries).length > 0) && <DailyPrompt />}
            </div>
            <div className="exercise-items">
               {exercises &&
               exerciseSummaries &&
               Object.keys(exerciseSummaries).length === 0 ? (
                  <ExerciseDashboardItem
                     key="gain-guru"
                     exerciseInfo={exercises.find(
                        ({ id }) => id === 'gain-guru'
                     )}
                     exerciseSummary={initialExerciseProgress}
                  />
               ) : (
                  exercises
                     // .filter(({ id }) => exerciseSummaries[id] != null)
                     .map((exerciseInfo) => (
                        <ExerciseDashboardItem
                           key={exerciseInfo.id}
                           exerciseInfo={exerciseInfo}
                           exerciseSummary={
                              exerciseSummaries[exerciseInfo.id] ??
                              initialExerciseProgress
                           }
                        />
                     ))
               )}
            </div>
         </DashboardStyles>
      </>
   );
};

function CoursePrompt({ courseDocument, courseProgress }) {
   const { lastCompleteLesson, nextLesson } = useProcessedLessonProgress(
      courseDocument,
      courseProgress
   );

   return (
      <article>
         <h2>{lastCompleteLesson == null ? 'Start' : 'Continue'} Learning</h2>
         <h3
            style={{
               marginBottom: '0em',
               whiteSpace: 'nowrap',
               textOverflow: 'ellipsis',
               overflow: 'hidden',
            }}
         >
            {courseDocument.title}
         </h3>
         <p
            style={{
               marginBottom: '1em',
               whiteSpace: 'nowrap',
               textOverflow: 'ellipsis',
               overflow: 'hidden',
            }}
         >
            {nextLesson.title}
         </p>
         <Button
            as={Link}
            className="button-cta"
            to={`/app/course/${courseDocument.id}/${nextLesson.id}`}
         >
            Go to Lesson
         </Button>
         <Button
            as={Link}
            // className="button-cta"
            to={`/app/course/${courseDocument.id}`}
            style={{ marginLeft: '1em' }}
         >
            Course Details
         </Button>
      </article>
   );
}

const DailyPrompt = () => {
   const dailyAvailable = useMemo(() => {
      const lastCompleted = localStorage.getItem('eiauth-lastDailyComplete');

      return (
         lastCompleted == null ||
         // there is an edge case where the daily won't show up in future months if completed on the same day of a previous month, but idgaf
         new Date().getDate() !== new Date(lastCompleted).getDate()
      );
   }, []);

   if (!dailyAvailable) {
      return null;
   }

   return (
      <article>
         <h2>Daily Practice</h2>
         <p style={{ marginBottom: '1em' }}>
            Play a different set of randomly selected exercises each day to
            evenly hone your skills!
         </p>
         <Button as={Link} className="button-cta" to={`/app/daily-practice`}>
            Start Daily Practice
         </Button>
         {/* <Button
            as={Link}
            // className="button-cta"
            to={`/app/daily-practice`}
            style={{ marginLeft: '1em' }}
         >
            Settings
         </Button> */}
      </article>
   );
};

export { Dashboard };
